<template>
  <div class="reward-checklist">
    <div class="reward-checklist__head">
      <div class="reward-checklist__count">
        <span class="reward-checklist__count-num">{{ selectedCount }}</span>
        <span class="reward-checklist__count-sep">of</span>
        <span class="reward-checklist__count-total">{{ rewards.length }}</span>
        <span class="reward-checklist__count-label">selected</span>
      </div>
      <button
        type="button"
        class="reward-checklist__toggle-all"
        :disabled="!visibleRewards.length"
        @click="toggleAllVisible"
      >
        {{ allVisibleSelected ? clearLabel : selectLabel }}
      </button>
    </div>

    <div
      v-if="categoryChips.length > 1"
      class="reward-checklist__filters"
      role="tablist"
      aria-label="Filter rewards by category"
    >
      <button
        v-for="chip in categoryChips"
        :key="chip.key"
        type="button"
        role="tab"
        :aria-selected="activeCategory === chip.key ? 'true' : 'false'"
        class="reward-chip"
        :class="[
          `reward-chip--${chip.tone}`,
          { 'reward-chip--active': activeCategory === chip.key }
        ]"
        @click="activeCategory = chip.key"
      >
        <span class="reward-chip__label">{{ chip.label }}</span>
        <span class="reward-chip__count">{{ chip.count }}</span>
      </button>
    </div>

    <div v-if="!rewards.length" class="reward-checklist__empty">
      No reward templates available. Your team can redeem custom rewards once set up.
    </div>

    <div v-else-if="!visibleRewards.length" class="reward-checklist__empty">
      No rewards in this category.
    </div>

    <ul
      v-else
      class="reward-checklist__grid"
      role="listbox"
      aria-multiselectable="true"
    >
      <li
        v-for="reward in visibleRewards"
        :key="rewardKey(reward)"
        class="reward-card"
        :class="{ 'reward-card--selected': isSelected(reward) }"
        role="option"
        :aria-selected="isSelected(reward) ? 'true' : 'false'"
        tabindex="0"
        @click="toggle(reward)"
        @keydown.space.prevent="toggle(reward)"
        @keydown.enter.prevent="toggle(reward)"
      >
        <div class="reward-card__media">
          <img
            v-if="rewardImage(reward)"
            :src="rewardImage(reward)"
            :alt="rewardName(reward)"
            class="reward-card__img"
            loading="lazy"
          />
          <div
            v-else
            class="reward-card__img reward-card__img--placeholder"
            aria-hidden="true"
          >
            <span>{{ rewardInitial(reward) }}</span>
          </div>

          <span class="reward-card__check" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="12" height="12">
              <path
                d="M3 8.5l3.2 3.2L13 4.8"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>

          <span
            v-if="rewardCategory(reward)"
            class="reward-card__pill"
            :class="`reward-card__pill--${rewardCategory(reward).tone}`"
          >
            {{ rewardCategory(reward).label }}
          </span>
        </div>

        <div class="reward-card__body">
          <div class="reward-card__name">{{ rewardName(reward) }}</div>
          <div v-if="rewardCost(reward).value" class="reward-card__cost">
            <svg
              class="reward-card__cost-icon"
              viewBox="0 0 16 16"
              width="12"
              height="12"
              aria-hidden="true"
            >
              <path
                d="M8 1.4L13.6 5.6 11.4 13.4 4.6 13.4 2.4 5.6Z"
                fill="currentColor"
                opacity="0.85"
              />
              <path
                d="M5.4 5.6 L10.6 5.6 L8 1.4 Z"
                fill="#ffffff"
                opacity="0.45"
              />
            </svg>
            <span class="reward-card__cost-num">{{ formatNum(rewardCost(reward).value) }}</span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * RewardChecklist — selectable grid of global reward templates.
 *
 * Data shape (from register/rewardMaster — Firebase /rewards where
 * company='global'):
 *   { reward_en, reward, coins, gems, image|picture, tag: ["0".."3"], newtype }
 *
 * Tag → category mapping comes from rewards.vue + reward.ts:
 *   "0" Gift Cards · "1" Team Building · "2" Doing Good · "3" Perks
 */

const CATEGORY_DEF = {
  '0': { label: 'Gift Cards',    tone: 'gold' },
  '1': { label: 'Team Building', tone: 'sky' },
  '2': { label: 'Doing Good',    tone: 'mint' },
  '3': { label: 'Perks',         tone: 'brand' },
}

export default {
  name: 'RewardChecklist',
  props: {
    rewards:     { type: Array, default: () => [] },
    selectedIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeCategory: 'all',
    }
  },
  computed: {
    selectedLookup() {
      const map = Object.create(null)
      for (const id of this.selectedIds) map[id] = true
      return map
    },
    selectedCount() {
      return this.selectedIds.length
    },
    categoryCounts() {
      const counts = { '0': 0, '1': 0, '2': 0, '3': 0 }
      for (const r of this.rewards) {
        const k = this.categoryKey(r)
        if (k && counts[k] !== undefined) counts[k] += 1
      }
      return counts
    },
    categoryChips() {
      const chips = [
        { key: 'all', label: 'All', tone: 'neutral', count: this.rewards.length },
      ]
      for (const k of Object.keys(CATEGORY_DEF)) {
        const count = this.categoryCounts[k]
        if (!count) continue
        chips.push({ key: k, label: CATEGORY_DEF[k].label, tone: CATEGORY_DEF[k].tone, count })
      }
      return chips
    },
    visibleRewards() {
      if (this.activeCategory === 'all') return this.rewards
      return this.rewards.filter((r) => this.categoryKey(r) === this.activeCategory)
    },
    visibleSelectedCount() {
      let n = 0
      for (const r of this.visibleRewards) {
        if (this.isSelected(r)) n += 1
      }
      return n
    },
    allVisibleSelected() {
      return this.visibleRewards.length > 0
        && this.visibleSelectedCount >= this.visibleRewards.length
    },
    selectLabel() {
      return this.activeCategory === 'all' ? 'Select all' : 'Select category'
    },
    clearLabel() {
      return this.activeCategory === 'all' ? 'Clear' : 'Clear category'
    },
  },
  methods: {
    rewardKey(r) {
      if (!r) return ''
      return r.key || r.id || r._id || r.reward_en || r.name || ''
    },
    rewardName(r) {
      if (!r) return ''
      return r.reward_en || r.name || r.reward || (r.names && (r.names.en || r.names.th)) || 'Reward'
    },
    rewardImage(r) {
      if (!r) return ''
      // Already-derived URL on the record (rewards.vue does this for the
      // authed app) wins.
      if (r.img)        return r.img
      if (r.image)      return r.image
      if (r.picture)    return r.picture
      if (r.imageUrl)   return r.imageUrl
      // Otherwise the record stores a Firebase Storage path under `filepath`
      // (e.g. "images/reward_company/global/abc/foo.png") — derive the
      // public URL the same way reward.ts does for company rewards.
      if (r.filepath && typeof r.filepath === 'string') {
        return this.firebaseStorageUrl(r.filepath)
      }
      return ''
    },
    firebaseStorageUrl(path) {
      const bucket = 'people2-admin.appspot.com'
      const prefix = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/`
      const encoded = String(path).replace(/\//g, '%2F')
      return `${prefix}${encoded}?alt=media`
    },
    rewardInitial(r) {
      const n = this.rewardName(r)
      return (n && n[0] ? n[0] : '?').toUpperCase()
    },
    categoryKey(r) {
      if (!r) return ''
      const tag = r.tag
      if (Array.isArray(tag) && tag.length) return String(tag[0])
      if (typeof tag === 'string' || typeof tag === 'number') return String(tag)
      return ''
    },
    rewardCategory(r) {
      const k = this.categoryKey(r)
      if (!k || !CATEGORY_DEF[k]) return null
      return CATEGORY_DEF[k]
    },
    rewardCost(r) {
      if (!r) return { unit: '', value: 0 }
      const gems = Number(r.gems) || 0
      const coins = Number(r.coins) || 0
      if (gems > 0) return { unit: 'gems', value: gems }
      if (coins > 0) return { unit: 'coins', value: coins }
      // Legacy fallbacks just in case.
      const cost = Number(r.cost) || 0
      const points = Number(r.points) || 0
      if (cost > 0) return { unit: 'gems', value: cost }
      if (points > 0) return { unit: 'gems', value: points }
      return { unit: '', value: 0 }
    },
    formatNum(n) {
      const num = Number(n) || 0
      if (num >= 1000) return num.toLocaleString()
      return String(num)
    },
    isSelected(r) {
      return !!this.selectedLookup[this.rewardKey(r)]
    },
    toggle(reward) {
      const id = this.rewardKey(reward)
      if (!id) return
      const next = this.selectedIds.slice()
      const i = next.indexOf(id)
      if (i === -1) next.push(id)
      else next.splice(i, 1)
      this.$emit('input', next)
    },
    toggleAllVisible() {
      if (!this.visibleRewards.length) return
      const visibleKeys = this.visibleRewards.map((r) => this.rewardKey(r)).filter(Boolean)
      if (this.allVisibleSelected) {
        const visibleSet = new Set(visibleKeys)
        const next = this.selectedIds.filter((id) => !visibleSet.has(id))
        this.$emit('input', next)
      } else {
        const next = this.selectedIds.slice()
        for (const k of visibleKeys) {
          if (next.indexOf(k) === -1) next.push(k)
        }
        this.$emit('input', next)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../_shared.scss';

.reward-checklist {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* === Header bar ============================================== */
.reward-checklist__head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: var(--signup-surface-subtle);
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  backdrop-filter: blur(6px);
}

.reward-checklist__count {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-family: 'Lato', system-ui, sans-serif;
  color: var(--signup-text);
}

.reward-checklist__count-num {
  font-size: 18px;
  font-weight: 800;
  color: var(--signup-brand);
}

.reward-checklist__count-sep,
.reward-checklist__count-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--signup-text-muted);
}

.reward-checklist__count-total {
  font-size: 13px;
  font-weight: 800;
  color: var(--signup-text);
}

.reward-checklist__toggle-all {
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: var(--signup-brand);
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 150ms ease;

  &:hover:not(:disabled) { background: rgba(108, 48, 204, 0.06); }
  &:disabled { color: var(--signup-text-muted); cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--signup-gold); outline-offset: 2px; }
}

/* === Category filter chips =================================== */
.reward-checklist__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.reward-chip {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 12px;
  border: 1px solid var(--signup-border-soft);
  border-radius: 999px;
  background: var(--signup-surface);
  color: var(--signup-text-soft);
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease,
              border-color 140ms ease, box-shadow 140ms ease;

  &:hover { color: var(--signup-text); border-color: var(--signup-lavender); }
  &:focus-visible { outline: 2px solid var(--signup-gold); outline-offset: 2px; }
}

.reward-chip__count {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(30, 10, 60, 0.06);
  color: var(--signup-text-soft);
  font-size: 11px;
  font-weight: 800;
}

/* Active state — neutral fallback */
.reward-chip--active {
  background: var(--signup-brand);
  border-color: var(--signup-brand);
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(108, 48, 204, 0.20),
              0 0 0 3px rgba(108, 48, 204, 0.12);

  .reward-chip__count {
    background: rgba(255, 255, 255, 0.22);
    color: #ffffff;
  }
}

/* Tone overrides for active state */
.reward-chip--gold.reward-chip--active {
  background: var(--signup-gold);
  border-color: var(--signup-gold);
  color: #6b4500;
  box-shadow: 0 1px 2px rgba(247, 185, 36, 0.30),
              0 0 0 3px rgba(247, 185, 36, 0.18);
  .reward-chip__count { background: rgba(0, 0, 0, 0.10); color: #6b4500; }
}

.reward-chip--sky.reward-chip--active {
  background: #5DADE2;
  border-color: #5DADE2;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(93, 173, 226, 0.28),
              0 0 0 3px rgba(93, 173, 226, 0.16);
}

.reward-chip--mint.reward-chip--active {
  background: var(--signup-success);
  border-color: var(--signup-success);
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(58, 196, 125, 0.28),
              0 0 0 3px rgba(58, 196, 125, 0.16);
}

/* === Grid =================================================== */
.reward-checklist__grid {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (min-width: 520px) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 760px) { grid-template-columns: repeat(4, 1fr); }
}

.reward-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 150ms ease, box-shadow 150ms ease,
              border-color 150ms ease, background 150ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: var(--signup-brand-light);
    box-shadow: var(--signup-shadow-card);

    .reward-card__img { transform: scale(1.04); }
  }
  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 2px;
  }
}

.reward-card--selected {
  border-color: var(--signup-brand);
  box-shadow:
    0 0 0 2px rgba(108, 48, 204, 0.12),
    var(--signup-shadow-card);
}

/* === Hero image ============================================== */
.reward-card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, #efe8fc 0%, #fff6d6 100%);
  overflow: hidden;
}

/* Subtle bottom shading so overlay badges always read */
.reward-card__media::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 60%,
    rgba(0, 0, 0, 0.18) 100%
  );
  pointer-events: none;
}

.reward-card__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.reward-card__img--placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--signup-brand);
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 26px;
  font-weight: 900;
  letter-spacing: 0.02em;
  /* Placeholder doesn't need the bg twice */
  background: transparent;
}

/* === Selection check (overlays image, top-right) ============= */
.reward-card__check {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1.5px solid rgba(255, 255, 255, 0.9);
  color: transparent;
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 2px rgba(30, 10, 60, 0.14);
  transition:
    background 160ms ease,
    border-color 160ms ease,
    color 160ms ease,
    transform 160ms ease;
}

.reward-card--selected .reward-card__check {
  background: var(--signup-brand);
  border-color: var(--signup-brand);
  color: #ffffff;
  animation: reward-check-pop 180ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
  box-shadow:
    0 1px 2px rgba(30, 10, 60, 0.18),
    0 0 0 3px rgba(108, 48, 204, 0.18);
}

@keyframes reward-check-pop {
  from { transform: scale(0.4); }
  to   { transform: scale(1); }
}

/* === Category pill (overlays image, bottom-left) ============= */
.reward-card__pill {
  position: absolute;
  left: 6px;
  bottom: 6px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 7px;
  border-radius: 999px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  backdrop-filter: blur(6px);
  box-shadow: 0 1px 2px rgba(30, 10, 60, 0.10);
}

.reward-card__pill--gold {
  background: rgba(255, 244, 207, 0.92);
  color: #8a5a00;
}
.reward-card__pill--sky {
  background: rgba(220, 238, 250, 0.92);
  color: #1f5d83;
}
.reward-card__pill--mint {
  background: rgba(216, 244, 228, 0.92);
  color: #1f6b46;
}
.reward-card__pill--brand {
  background: rgba(231, 220, 248, 0.92);
  color: var(--signup-brand-deep);
}

/* === Body ==================================================== */
.reward-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px 10px;
  min-width: 0;
}

.reward-card__name {
  min-width: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--signup-text);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.reward-card__cost {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--signup-text-soft);
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.reward-card__cost-icon {
  color: var(--signup-brand-light);
  flex-shrink: 0;
}

.reward-card__cost-num {
  color: var(--signup-text);
}

.reward-card--selected .reward-card__cost-icon {
  color: var(--signup-brand);
}

/* === Empty / fallback ======================================== */
.reward-checklist__empty {
  padding: 20px;
  border: 1px dashed var(--signup-border-soft);
  border-radius: 14px;
  background: var(--signup-surface-subtle);
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 14px;
  color: var(--signup-text-muted);
  text-align: center;
}
</style>
